import {
  useState,
  PropsWithChildren,
  Suspense,
  ComponentProps,
  useCallback,
} from "react"
import { useParams } from "react-router"
import { useTranslation } from "react-i18next"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion.tsx"
import { cn } from "@/lib/utils"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import { useGetOpexSelectedCategories } from "@/feature/financial-model/ecommerce/opex/api"
import { SelectedCategoryContent } from "@/feature/financial-model/ecommerce/opex/components/setup/selected-category-content.tsx"
import { CategoryBadgesContent } from "@/feature/financial-model/ecommerce/opex/components/setup/category-badges-content.tsx"
import { OpexModellingPreviewDialog } from "@/feature/financial-model/ecommerce/opex/components/setup/modelling-preview-table.tsx"

const DEFAULT_ACCORDION_ITEMS = {
  CATEGORIES: `categories`,
}

const StyledAccordionItem = ({
  className,
  ...rest
}: ComponentProps<typeof AccordionItem>) => (
  <AccordionItem
    className={cn(`bg-white p-6 shadow-loose rounded-lg`, className)}
    {...rest}
  />
)

const StyledAccordionTrigger = ({
  className,
  ...rest
}: ComponentProps<typeof AccordionTrigger>) => (
  <AccordionTrigger className={cn(`py-0`, className)} {...rest} />
)

const CategoryAccordionContent = ({
  className,
  ...rest
}: ComponentProps<typeof AccordionContent>) => (
  <AccordionContent className={cn(`pt-4`, className)} {...rest} />
)

const AccordionItemTitle = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => (
  <h3 className={cn(`text-lg font-medium text-primary`, className)}>
    {children}
  </h3>
)

const AccordionItemDescription = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => (
  <p className={cn(`text-base text-secondary font-normal`, className)}>
    {children}
  </p>
)

const OpexSetupRoute = () => {
  const { t } = useTranslation(`opex`)
  const { id: financialModelId } = useParams<{ id: string }>()
  if (!financialModelId) {
    throw new Error(`OpexSetupRoute > Financial model id is missing`)
  }

  const { data: opexSelectedCategories } = useGetOpexSelectedCategories({
    financialModelId,
  })

  const [openedAccordionItems, setOpenedAccordionItems] = useState<string[]>([
    DEFAULT_ACCORDION_ITEMS.CATEGORIES,
    ...opexSelectedCategories.map((cat) => cat.opex_category.name),
  ])

  const onNewCategoryBadgeAdded = useCallback((newCategoryName: string) => {
    setOpenedAccordionItems((prev) => [...prev, newCategoryName])
  }, [])

  return (
    <Accordion
      type="multiple"
      value={openedAccordionItems}
      onValueChange={(newSteps: string[]) => {
        setOpenedAccordionItems(newSteps)
      }}
      className="flex flex-col gap-6"
    >
      <StyledAccordionItem value={DEFAULT_ACCORDION_ITEMS.CATEGORIES}>
        <StyledAccordionTrigger>
          <div className={`flex flex-col items-start text-left`}>
            <AccordionItemTitle className={`mb-1`}>
              {t(`opex.setup.categories_accordion_item.title`)}
            </AccordionItemTitle>
            <AccordionItemDescription>
              {t(`opex.setup.categories_accordion_item.subtitle`)}
            </AccordionItemDescription>
          </div>
        </StyledAccordionTrigger>
        <AccordionContent className={`pt-0 data-[state=open]:pt-6`}>
          <Suspense fallback={<Skeleton className={`h-[215px]`} />}>
            <CategoryBadgesContent
              financialModelId={financialModelId}
              onNewCategoryAdded={onNewCategoryBadgeAdded}
            />
          </Suspense>
        </AccordionContent>
      </StyledAccordionItem>
      <OpexModellingPreviewDialog>
        {opexSelectedCategories.map((cat) => (
          <StyledAccordionItem key={cat.id} value={cat.opex_category.name}>
            <StyledAccordionTrigger>
              <AccordionItemTitle>{cat.opex_category.name}</AccordionItemTitle>
            </StyledAccordionTrigger>
            <CategoryAccordionContent>
              <Suspense fallback={<Skeleton className={`h-[80px]`} />}>
                <SelectedCategoryContent
                  selectedCategoryId={cat.id}
                  financialModelId={financialModelId}
                />
              </Suspense>
            </CategoryAccordionContent>
          </StyledAccordionItem>
        ))}
      </OpexModellingPreviewDialog>
    </Accordion>
  )
}

export default OpexSetupRoute
